import { useState } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { CompactTablePagination, EmptyState, ErrorState, LoadingState, TablePageShell } from '@/shared/ui';

import { useDeactivatePricingRule, usePricingRules } from '../../hooks';
import { formatPrice } from '../../lib/format';
import { RULE_TYPE_LABELS, RULE_TYPE_OPTIONS } from '../../model/options';
import type { PricingRuleResponse } from '../../model/types';
import type { ToastSeverity } from '../../lib/useToast';
import { RuleFormDialog } from './RuleFormDialog';

interface PanelProps {
  canManage: boolean;
  onResult: (severity: ToastSeverity, message: string) => void;
}

function ruleValue(rule: PricingRuleResponse): string {
  if (rule.discountPercent != null) return `−${rule.discountPercent}%`;
  if (rule.priceOverride != null) return formatPrice(rule.priceOverride);
  if (rule.taxRate != null) return `${Math.round(rule.taxRate * 100)}% налог`;
  return '—';
}

/** Pricing rules — list, create, update (new version) and deactivate. */
export function PricingRulesPanel({ canManage, onResult }: PanelProps) {
  const [ruleType, setRuleType] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PricingRuleResponse | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<PricingRuleResponse | null>(null);

  const query = usePricingRules({ ruleType: ruleType || undefined, page, size });
  const deactivate = useDeactivatePricingRule();
  const rules = query.data?.content ?? [];

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label="Тип правила"
          value={ruleType}
          onChange={(e) => {
            setRuleType(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">Все типы</MenuItem>
          {RULE_TYPE_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <Stack flex={1} />
        {canManage ? (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
            Добавить правило
          </Button>
        ) : null}
      </Stack>

      {query.isLoading ? (
        <LoadingState label="Загрузка правил…" />
      ) : query.isError ? (
        <ErrorState
          title="Не удалось загрузить правила"
          message={query.error?.message ?? 'Произошла ошибка.'}
          onRetry={() => void query.refetch()}
        />
      ) : rules.length === 0 ? (
        <EmptyState
          title="Правил пока нет"
          description="Создайте правило скидки, промокода или налога."
          icon={<RuleRoundedIcon />}
          action={
            canManage ? (
              <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
                Добавить правило
              </Button>
            ) : undefined
          }
        />
      ) : (
        <TablePageShell
          footer={
            <CompactTablePagination
              count={query.data?.totalElements ?? 0}
              page={page}
              rowsPerPage={size}
              hideRowsPerPage
              onPageChange={(_e, next) => setPage(next)}
              onRowsPerPageChange={(e) => {
                setSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
            />
          }
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Товар</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>Значение</TableCell>
                  <TableCell align="right">Приоритет</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((rule) => (
                  <TableRow key={rule.id} hover>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: 'JetBrains Mono, monospace' }}
                      >
                        {rule.productId}
                      </Typography>
                    </TableCell>
                    <TableCell>{RULE_TYPE_LABELS[rule.ruleType] ?? rule.ruleType}</TableCell>
                    <TableCell>{ruleValue(rule)}</TableCell>
                    <TableCell align="right">{rule.priority}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={rule.active ? 'success' : 'default'}
                        label={rule.active ? 'Активно' : 'Отключено'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title={canManage ? 'Редактировать' : 'Просмотр'}>
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="Редактировать правило"
                            onClick={() => {
                              setEditing(rule);
                              setDialogOpen(true);
                            }}
                          >
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Отключить">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!canManage || !rule.active}
                              aria-label="Отключить правило"
                              onClick={() => setDeactivateTarget(rule)}
                            >
                              <BlockRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TablePageShell>
      )}

      <RuleFormDialog
        open={dialogOpen}
        rule={editing}
        onClose={() => setDialogOpen(false)}
        onResult={onResult}
      />

      <Dialog open={deactivateTarget !== null} onClose={() => setDeactivateTarget(null)}>
        <DialogTitle>Отключить правило?</DialogTitle>
        <DialogContent>
          <DialogContentText>Правило перестанет применяться к ценам.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateTarget(null)} disabled={deactivate.isPending}>
            Отмена
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deactivate.isPending}
            onClick={() => {
              if (!deactivateTarget) return;
              deactivate.mutate(deactivateTarget.id, {
                onSuccess: () => {
                  setDeactivateTarget(null);
                  onResult('success', 'Правило отключено.');
                },
                onError: (error) => onResult('error', error.message),
              });
            }}
          >
            Отключить
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
